/* eslint-disable arrow-body-style */
import _ from 'underscore';
import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  Card,
  CardHeader,
  Slider,
  Switch,
  Text,
  mergeClasses,
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody } from '@fluentui/react-message-bar';
import { VideoBackgroundEffect20Regular } from '@fluentui/react-icons';

import { ImagePreviewView } from '../image-preview-view';
import { useStyles } from './style';

export const ImageAdjustView = ({
  resultImage,
  toggleBackgroundBlur,
  setFilterIndex,
  changeBackgroundBlurLevel,
  imagesWithFilter,
  i18n,
  backgroundBlurEnabled,
  backgroundBlurLevel,
  goToBackgroundChangeView,
  filterIndex,
  isAIGCRegionRestricted,
  notificationApi,
  permissions,
}) => {
  const imageAdjustViewStyles = useStyles();
  const isAdsStudioBackgroundChangeEnabled = _.get(permissions, ['dynamic', 'AdsStudioBackgroundChange'], false);

  const buttonsConfig = useMemo(() => (isAdsStudioBackgroundChangeEnabled ? [{
    text: i18n.getString(_TL_('Replace background')),
    icon: VideoBackgroundEffect20Regular,
    onClick: () => {
      if (isAIGCRegionRestricted) {
        notificationApi.showWarning(i18n.getString(_TL_('This feature isn\'t available in all markets.')));
      } else {
        goToBackgroundChangeView();
      }
    },
  }] : []), [goToBackgroundChangeView, i18n, isAIGCRegionRestricted, notificationApi, isAdsStudioBackgroundChangeEnabled]);

  const timerRef = useRef(null);

  const handleChangeBlur = useCallback((event, data) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      changeBackgroundBlurLevel(data.value);
    }, 300);
  }, [changeBackgroundBlurLevel]);

  return (
    <div className={mergeClasses(imageAdjustViewStyles, 'image-adjust-view')}>
      <div className="card-wrapper">
        <Card className="filters-card">
          <CardHeader
            role="heading"
            aria-level="2"
            header={<Text weight="semibold">{i18n.getString(_TL_('Filters'))}</Text>}
          />
          {
            imagesWithFilter && imagesWithFilter.length > 1 &&
            <div className="filters-list" role="listbox" aria-label={i18n.getString(_TL_('Filters'))}>
              {
                imagesWithFilter.map((image, index) => (
                  <button
                    key={image.id}
                    onClick={() => setFilterIndex(index)}
                    className={classnames('filter-wrapper', filterIndex === index && 'selected')}
                    aria-selected={filterIndex === index}
                    role="option"
                    aria-label={image.filter}
                  >
                    <img src={image.url} alt="filter" />
                    <div className="filter-name">{image.filter}</div>
                  </button>
                ))
              }
            </div>
          }
          {
            imagesWithFilter && imagesWithFilter.length === 1 &&
            <MessageBar intent="error">
              <MessageBarBody>
                {i18n.getString(_TL_('No filters available'))}
              </MessageBarBody>
            </MessageBar>
          }
        </Card>
        <Card className="blur-card">
          <CardHeader
            header={
              <div className="blur-header">
                <VideoBackgroundEffect20Regular />
                <Text weight="regular">{i18n.getString(_TL_('Background blur'))}</Text>
              </div>}
            action={<Switch
              className="blur-switch"
              checked={backgroundBlurEnabled}
              onChange={() => { toggleBackgroundBlur(!backgroundBlurEnabled); }}
              label={backgroundBlurEnabled ? i18n.getString(_TL_('On')) : i18n.getString(_TL_('Off'))}
              aria-label={i18n.getString(_TL_('Background blur'))}
            />}
          />
          {
            backgroundBlurEnabled &&
            <div className="blur-slider">
              <div className="blur-slider-value-wrapper">
                <div className="blur-slider-label">{i18n.getString(_TL_('Blur amount'))}</div>
                <div className="blur-slider-value">{backgroundBlurLevel}</div>
              </div>
              <Slider
                min={0}
                max={100}
                defaultValue={50}
                value={backgroundBlurLevel}
                onChange={handleChangeBlur}
                className="blur-slider-control"
                width="100%"
                aria-label={i18n.getString(_TL_('Blur amount'))}
              />
            </div>
          }
        </Card>
      </div>
      {
        resultImage && resultImage.url &&
        <ImagePreviewView
          url={resultImage.url}
          buttonsConfig={buttonsConfig}
        />
      }
    </div>
  );
};

ImageAdjustView.propTypes = {
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({}).isRequired,
  }).isRequired,
  resultImage: PropTypes.shape({
    url: PropTypes.string,
  }),
  toggleBackgroundBlur: PropTypes.func,
  setFilterIndex: PropTypes.func,
  changeBackgroundBlurLevel: PropTypes.func,
  imagesWithFilter: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    url: PropTypes.string,
    filter: PropTypes.string,
  })),
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }),
  backgroundBlurEnabled: PropTypes.bool,
  backgroundBlurLevel: PropTypes.number,
  goToBackgroundChangeView: PropTypes.func,
  isAIGCRegionRestricted: PropTypes.bool,
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func,
  }),
};

ImageAdjustView.defaultProps = {
  resultImage: null,
  toggleBackgroundBlur: () => {},
  setFilterIndex: () => {},
  changeBackgroundBlurLevel: () => {},
  imagesWithFilter: [],
  i18n: {},
  backgroundBlurEnabled: false,
  backgroundBlurLevel: 0,
  goToBackgroundChangeView: () => {},
  isAIGCRegionRestricted: false,
  notificationApi: {},
};
